// Cloudflare Turnstile browser-proof acquisition.
//
// ISS-260819-epq3 Phase 3 ("PR B — browser adoption"), spec §5 "Browser
// helper": `.claude/docs/plans/2026-08-25-public-endpoint-browser-proof-spec.md`.
//
// This module ONLY acquires a fresh, server-verifiable Turnstile token — it
// NEVER decides policy (accept/reject/quarantine/enforce). Every consumer
// (contact form, dashboard passwordless) calls acquireBrowserProof()
// immediately before its side-effecting request and forwards the resulting
// token (or nothing, on acquisition failure) to the server, which is always
// the authoritative decision-maker (spec D3/D4). Acquisition failure must
// never block the caller from submitting — "fail closed" is a SERVER policy
// applied to a missing/invalid proof, not a client-side refusal to send.
//
// Widget option names cited from Cloudflare's documented client-side
// rendering API:
//   - Explicit rendering, execution mode, and the lifecycle callback set
//     (`callback` / `error-callback` / `expired-callback` / `timeout-callback`
//     / `unsupported-callback`):
//     https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
//   - `appearance` values (`always` | `execute` | `interaction-only`) and the
//     full per-widget configuration option set:
//     https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/widget-configurations/
//   - Client-side error/troubleshooting behaviour (script load failures,
//     unsupported browsers):
//     https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const DEFAULT_TIMEOUT_MS = 8000;
// Module-scoped so the script is requested at most ONCE per page load, no
// matter how many times acquireBrowserProof() is subsequently called (spec
// §5: "loads ... once"). A fast network ERROR self-heals via onerror below.
// A STALLED load (the <script> tag fires neither onload nor onerror —
// network hang, a silent ad-blocker drop, a CSP block some browsers apply
// without an error event, ...) does NOT self-heal that way, because neither
// callback ever fires; it is evicted separately, from
// acquireBrowserProof()'s own overall deadline (see evictStalledScriptLoad
// below) — codex review P1, 2026-08-25.
let scriptLoadPromise = null;
let scriptElement = null;
function loadTurnstileScript() {
    if (scriptLoadPromise) {
        return scriptLoadPromise;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    const promise = new Promise((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => {
            // Only clear module state if THIS load is still the current one. A
            // stalled load already evicted by evictStalledScriptLoad() (module
            // state now pointing at nothing, or at a NEWER retry someone else
            // already kicked off) must not have this late onerror callback
            // clobber that newer state.
            if (scriptLoadPromise === promise) {
                scriptLoadPromise = null;
                scriptElement = null;
            }
            reject(new Error("turnstile_script_load_failed"));
        };
    });
    scriptLoadPromise = promise;
    scriptElement = script;
    document.head.appendChild(script);
    return promise;
}
/**
 * Evicts a STALLED script load — one whose <script> tag has fired neither
 * onload nor onerror by an acquisition's deadline — so the NEXT
 * acquireBrowserProof() call re-injects a fresh <script> instead of
 * awaiting the same permanently-stalled promise for the rest of the page's
 * life. Removes the tag from the DOM too, not just the cached promise.
 *
 * Called ONLY from acquireBrowserProof()'s own overall timeout, passing the
 * EXACT promise instance that call was awaiting. The guard
 * (`scriptLoadPromise !== stalledPromise`) matters for two races: (a) two
 * concurrent acquireBrowserProof() calls both awaiting the same stalled
 * load — whichever timeout fires second must not re-evict a promise the
 * first already cleared (or a fresh retry a third call already started);
 * (b) this stalled script's onerror firing LATE, after eviction already
 * ran — onerror's own matching guard (above) handles that side.
 */
function evictStalledScriptLoad(stalledPromise) {
    if (scriptLoadPromise !== stalledPromise) {
        return;
    }
    scriptElement?.remove();
    scriptElement = null;
    scriptLoadPromise = null;
}
/**
 * Acquires a fresh Cloudflare Turnstile Invisible token for one action.
 *
 * Every call renders its OWN widget into its own detached, hidden container
 * (`execution: "execute"` — the widget never auto-runs on render; this
 * function calls `turnstile.execute()` itself) and removes both the widget
 * and the container once it settles — so every returned token is freshly
 * minted for this call and no widget/token state is ever cached or reused
 * across calls.
 *
 * Never decides policy: resolves `{ ok: true, token }` or a typed
 * `{ ok: false, reason }` and leaves what to do about it entirely to the
 * caller/server.
 */
export function acquireBrowserProof(opts) {
    const { siteKey, action, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
    // SSR-safe: no `window`/`document` means there is nothing to render.
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.resolve({ ok: false, reason: "unsupported" });
    }
    if (!siteKey) {
        return Promise.resolve({ ok: false, reason: "no_site_key" });
    }
    return new Promise((resolve) => {
        let settled = false;
        let widgetId;
        let container;
        const cleanup = () => {
            if (widgetId !== undefined && window.turnstile) {
                try {
                    window.turnstile.remove(widgetId);
                }
                catch {
                    // best-effort — the widget/container are being discarded regardless
                }
            }
            container?.remove();
        };
        const settle = (result) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            cleanup();
            resolve(result);
        };
        // ONE deadline for the WHOLE operation — script load, widget render, AND
        // execute/challenge (codex review P1, 2026-08-25). Previously the timer
        // only started AFTER `await loadTurnstileScript()` resolved, so a script
        // request whose <script> tag fired neither onload nor onerror (network
        // hang, a silent ad-blocker drop, a CSP block some browsers apply
        // without an error event, ...) hung this function forever — it never
        // even reached its own timer. Starting the timer here, before
        // `loadTurnstileScript()` is even called, covers every stage. On
        // timeout we ALSO evict any stalled script load (module cache + the
        // injected <script> tag itself) so the NEXT acquireBrowserProof() call
        // retries with a fresh <script> instead of awaiting the same
        // permanently-stalled promise for the rest of the page's life.
        const scriptPromise = loadTurnstileScript();
        const timer = setTimeout(() => {
            evictStalledScriptLoad(scriptPromise);
            settle({ ok: false, reason: "timeout" });
        }, timeoutMs);
        scriptPromise
            .then(() => {
            if (settled) {
                return;
            }
            const turnstile = window.turnstile;
            if (!turnstile) {
                settle({ ok: false, reason: "script_load_failed" });
                return;
            }
            const el = document.createElement("div");
            // Detached/hidden — this widget must never be visible. `appearance:
            // "interaction-only"` (below) already keeps Turnstile's own iframe
            // invisible unless Cloudflare decides interaction is required; this
            // is belt-and-braces so the container itself never occupies layout.
            el.style.position = "absolute";
            el.style.width = "0";
            el.style.height = "0";
            el.style.overflow = "hidden";
            el.setAttribute("aria-hidden", "true");
            document.body.appendChild(el);
            container = el;
            try {
                widgetId = turnstile.render(el, {
                    sitekey: siteKey,
                    action,
                    // Explicit rendering with execution: "execute" — the widget
                    // does NOT auto-run on render; execute() below triggers exactly
                    // one fresh challenge per acquireBrowserProof() call.
                    execution: "execute",
                    // Invisible widget: Turnstile's iframe stays hidden unless
                    // Cloudflare decides interaction is required (the documented
                    // invisible-widget appearance value).
                    appearance: "interaction-only",
                    callback: (token) => settle({ ok: true, token }),
                    "error-callback": () => settle({ ok: false, reason: "error" }),
                    "expired-callback": () => settle({ ok: false, reason: "expired" }),
                    "timeout-callback": () => settle({ ok: false, reason: "timeout" }),
                    "unsupported-callback": () => settle({ ok: false, reason: "unsupported" }),
                });
            }
            catch {
                settle({ ok: false, reason: "render_failed" });
                return;
            }
            if (widgetId === undefined) {
                settle({ ok: false, reason: "render_failed" });
                return;
            }
            try {
                turnstile.execute(widgetId);
            }
            catch {
                settle({ ok: false, reason: "render_failed" });
            }
        })
            .catch(() => {
            settle({ ok: false, reason: "script_load_failed" });
        });
    });
}
// Test-only escape hatch: forces the next acquireBrowserProof() call to
// re-request the script. Production code never calls this — the whole point
// of the module-scoped promise is that a real page never needs to.
export function __resetBrowserProofScriptStateForTests() {
    scriptLoadPromise = null;
    scriptElement = null;
}
